import type { Method } from "axios";
import type { BEResponse } from "client/api/backend/schemas";
import { backendApiClient } from "client/core/network/mutators/apiClient";
import { pick } from "lodash";
import { useCallback } from "react";
import type { ProviderInternalDynamicField } from "../../provider-parameters/ProviderParameters";

/**
 * Hook che ritorna la funzione per gestire
 * le chiamatate alle API di firma remota,
 * che richiedono i campi nell'header.
 */
export function useRemoteSignApi<
  T extends BEResponse,
  /**
   * Tipo di body da inviare nella richiesta.
   * Se non è necessario inviare un body, può essere `never` (default).
   */
  B extends Record<string, any> = never,
>(params: {
  method: Method;
  fields: ProviderInternalDynamicField[];
  apiUrl: string;
  skip?: boolean;
}) {
  const { method, fields, apiUrl, skip } = params;

  const apiFn = useCallback(
    async (
      dynamicParams: { [key: string]: string },
      body?: B,
      extraHeaders?: Record<string, string>,
    ) => {
      if (skip) return null;

      const requestInput = pick(
        dynamicParams,
        fields.map((p) => p.nome),
      );

      const { data } = await backendApiClient.request<T>({
        url: apiUrl,
        method,
        headers: {
          keyValueMap: Object.keys(requestInput)
            .flatMap((key) => [key, requestInput[key]])
            .join(","),
          ...(extraHeaders ?? {}),
        },
        data: body,
      });

      if (data.esito?.errore) {
        throw new Error(data.esito.errore.messaggioDiErrore); // prettier-ignore
      }
      return data;
    },
    [method, fields, apiUrl, skip],
  );

  return apiFn;
}
